import type { HREmployee } from '@cloud/db'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Eye, MoreHorizontal, Pencil, Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { EmployeeAvatar } from '@/components/hr/employee-avatar'
import { EmployeeForm } from '@/components/hr/employee-form'
import { StatusBadge, type StatusType } from '@/components/hr/status-badge'
import { ModuleGuard } from '@/components/module-guard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/hr/employees/')({
  component: EmployeesPage,
})

function EmployeesPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <EmployeesList />
    </ModuleGuard>
  )
}

function EmployeesList() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<string>('all')
  const [employmentType, setEmploymentType] = useState<string>('all')
  const [department, setDepartment] = useState<string>('all')
  const [sorting, setSorting] = useState<SortingState>([])
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<HREmployee | null>(null)

  const { data: departmentsData } = useQuery(orpc.hr.getDepartments.queryOptions({ input: {} }))
  const departments = (departmentsData ?? []) as { id: number; name: string }[]

  const { data, isLoading, refetch } = useQuery(
    orpc.hr.getEmployees.queryOptions({
      input: {
        page: 1,
        limit: 100,
        search: search || undefined,
        status:
          status !== 'all'
            ? (status as 'active' | 'inactive' | 'terminated' | undefined)
            : undefined,
        employmentType:
          employmentType !== 'all'
            ? (employmentType as 'full-time' | 'part-time' | 'contract' | undefined)
            : undefined,
        department: department !== 'all' && department ? Number(department) : undefined,
      },
    }),
  )

  const employees = (data?.data ?? []) as HREmployee[]

  const columnHelper = createColumnHelper<HREmployee>()

  const columns = [
    columnHelper.accessor('firstName', {
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Employee
          {column.getIsSorted() === 'asc' ? ' ↑' : column.getIsSorted() === 'desc' ? ' ↓' : ''}
        </Button>
      ),
      cell: ({ row }) => <EmployeeAvatar employee={row.original} showName />,
    }),
    columnHelper.accessor('departmentId', {
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Department
          {column.getIsSorted() === 'asc' ? ' ↑' : column.getIsSorted() === 'desc' ? ' ↓' : ''}
        </Button>
      ),
      cell: ({ getValue }) => {
        const deptId = getValue()
        const dept = departments.find((d) => d.id === deptId)
        return dept?.name ?? '-'
      },
    }),
    columnHelper.accessor('jobTitle', {
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Job Title
          {column.getIsSorted() === 'asc' ? ' ↑' : column.getIsSorted() === 'desc' ? ' ↓' : ''}
        </Button>
      ),
      cell: ({ getValue }) => getValue() ?? '-',
    }),
    columnHelper.accessor('employmentType', {
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Type
          {column.getIsSorted() === 'asc' ? ' ↑' : column.getIsSorted() === 'desc' ? ' ↓' : ''}
        </Button>
      ),
      cell: ({ getValue }) => <Badge variant="outline">{getValue() ?? '-'}</Badge>,
    }),
    columnHelper.accessor('status', {
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="-ml-4 h-8"
        >
          Status
          {column.getIsSorted() === 'asc' ? ' ↑' : column.getIsSorted() === 'desc' ? ' ↓' : ''}
        </Button>
      ),
      cell: ({ getValue }) => <StatusBadge status={(getValue() as StatusType) ?? 'active'} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <a href={`/hr/employees/${row.original.id}`}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleEdit(row.original)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDelete(row.original.id)}
              className="text-red-600"
            >
              Deactivate
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }),
  ]

  const table = useReactTable({
    data: employees,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  const deleteMutation = useMutation(
    orpc.hr.deleteEmployee.mutationOptions({
      onSuccess: () => {
        toast.success('Employee deactivated')
        refetch()
      },
      onError: (e) => toast.error(e.message),
    }),
  )

  const handleEdit = (employee: HREmployee) => {
    setEditingEmployee(employee)
    setIsFormOpen(true)
  }

  const handleDelete = async (employeeId: number) => {
    if (confirm('Are you sure you want to deactivate this employee?')) {
      await deleteMutation.mutateAsync({ id: employeeId })
    }
  }

  const handleFormSuccess = () => {
    refetch()
    setEditingEmployee(null)
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Employees</h1>
          <p className="text-muted-foreground">Manage your organization's employees</p>
        </div>
        <Button
          onClick={() => {
            setEditingEmployee(null)
            setIsFormOpen(true)
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
            <Select value={employmentType} onValueChange={setEmploymentType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="full-time">Full-time</SelectItem>
                <SelectItem value="part-time">Part-time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
              </SelectContent>
            </Select>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id.toString()}>
                    {dept.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No employees found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EmployeeForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employee={editingEmployee ?? undefined}
        onSuccess={handleFormSuccess}
      />
    </div>
  )
}
